import React, { useMemo } from "react";

import { concat, sortBy, uniqBy } from "lodash";

import useTranslations from "../../hooks/useTranslations";

import { H4, Link } from "../wombat-ui";
import { FollowUpPlate } from "./FollowUpPlate";
import { ReviewMinimalFragment, UserReviewFragment } from '../../graphql/generated/definitions';

type UserFollowUpsSectionProps = {
  user: UserReviewFragment;
};

const UserFollowUpsSection: React.FC<UserFollowUpsSectionProps> =
  ({ user }) => {
    const t = useTranslations();

    if (!user) {
      return null;
    }

    const ongoingReviews = useMemo(
      () =>
        sortBy(
          concat(
            user.reviewsAsCollaborator.filter((review) => new Date(review.endDate) > new Date()),
            user.reviewsAsReferent.filter((review) => new Date(review.endDate) > new Date())
          ),
          "updatedAt"
        ).reverse(),
      [user]
    );

    const finishedReviews = useMemo(
      () =>
        sortBy(
          concat(
            user.reviewsAsCollaborator.filter((review) => new Date(review.endDate) <= new Date()),
            user.reviewsAsReferent.filter((review) => new Date(review.endDate) <= new Date())
          ),
          "updatedAt"
        ).reverse(),
      [user]
    );

    const reviewMap: {
      key: "ongoingFollowUps" | "finishedFollowUps";
      values: ReviewMinimalFragment[];
    }[] = useMemo(() => (
      [
        { key: "ongoingFollowUps", values: uniqBy(ongoingReviews, "id") },
        { key: "finishedFollowUps", values: uniqBy(finishedReviews, "id") },
      ]
    ), [ongoingReviews, finishedReviews]);

    return (
      <div className="space-y-6">
        {reviewMap.map((reviews) => (
          <div key={reviews.key} id={`followUpPlates-${reviews.key}`}>
            <H4>{t(`collaborator.${reviews.key}` as const)}</H4>
            <div className="pt-2">
              {reviews.values.length === 0 ? (
                <div className="text-sm text-light-900">
                  <p>{t("collaborator.noFollowUps")}</p>
                </div>
              ) : (
                reviews.values.map((review) => (
                  <Link
                    className="no-underline text-dark-700"
                    to={`/reviews/${review.id}`}
                    key={review.id}
                  >
                    <FollowUpPlate review={review} user={user} />
                  </Link>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

export default UserFollowUpsSection;
